/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import styled, { css } from "styled-components";
import { Link } from "react-router-dom";
import { isActivationKey } from "../../hooks/a11y/activationKeys";

const StyledAnchor = styled((props) => <Link {...props} />)`
  ${(props) => sizeStyles[props.$size]}
  color: rgba(${({ theme }) => theme.colors.link.rgba});
  ${(props) => underlineStyles[props.$underline]}
  cursor: pointer;
`;

const underlineStyles = {
  always: css`
    text-decoration: underline;
  `,
  hover: css`
    text-decoration: none;

    @media (hover: hover) {
      &:hover {
        text-decoration: underline;
      }
    }
  `,
  never: css`
    text-decoration: none;
  `,
};

const sizeStyles = {
  default: css`
  font-size: calc(${({ theme }) => theme.fontSizes.p} * 0.875);
  line-height: 1.44;
`,
  small: css`
  font-size: calc(${({ theme }) => theme.fontSizes.p} * 0.75);
  line-height: 1.65;
`,
};

const Anchor = React.forwardRef(({ children, to, target, underline, size = "default", onClick, ...props }, ref) => {
  const handleAnchorClick = (e) => {
    e.stopPropagation();
    onClick?.(e);
    if (!to) e.currentTarget.blur();
  };

  // onClick-only anchor (no href): an <a> without href isn't focusable, so give
  // it button semantics + Enter/Space activation so keyboard users can reach it.
  const buttonProps =
    !to && onClick
      ? {
          role: "button",
          tabIndex: 0,
          onKeyDown: (e) => {
            if (isActivationKey(e)) {
              e.preventDefault();
              handleAnchorClick(e);
            }
          },
        }
      : {};

  return (
    <StyledAnchor
      as={to ? Link : "a"}
      ref={ref}
      target={target}
      to={to}
      onClick={handleAnchorClick}
      $underline={underline}
      $size={size}
      {...buttonProps}
      {...props}
    >
      {children}
    </StyledAnchor>
  );
});

export default Anchor;
